<template>
  <section class="page-shell">
    <div class="page-heading">
      <div>
        <h1>Políticas de validação</h1>
        <p>Regras simples que alimentam a validação de documentos e definem quando a revisão humana é obrigatória.</p>
      </div>
      <v-btn color="primary" @click="openDialog()">
        <v-icon left small>mdi-plus</v-icon>
        Criar política
      </v-btn>
    </div>

    <v-alert v-if="error" type="error" dense text>{{ error }}</v-alert>

    <section class="policy-template-panel data-panel">
      <div class="panel-title">
        <h2>Comece por um modelo</h2>
        <span class="table-caption">Clique em uma regra pronta, ajuste se necessário e publique. O objetivo é configurar em minutos.</span>
      </div>
      <div class="policy-template-grid">
        <button
          v-for="template in policyTemplates"
          :key="template.title"
          type="button"
          class="policy-template-card"
          @click="openDialog(null, template)"
        >
          <v-icon>{{ template.icon }}</v-icon>
          <strong>{{ template.title }}</strong>
          <span>{{ template.short }}</span>
        </button>
      </div>
    </section>

    <section class="policy-grid">
      <article v-for="policy in policies" :key="policy.id" class="data-panel policy-card">
        <div class="panel-title">
          <h2>{{ policy.title }}</h2>
          <v-chip small :color="policy.status === 'published' ? 'success' : 'grey'" text-color="white">{{ statusLabel(policy.status) }}</v-chip>
        </div>
        <p>{{ policy.summary || 'Sem resumo registrado.' }}</p>
        <div class="policy-meta">
          <span>{{ categoryLabel(policy.category) }}</span>
          <span>{{ policy.owner_role || 'Sem responsável' }}</span>
          <span>v{{ policy.version || '1.0' }}</span>
        </div>
        <div class="policy-actions">
          <v-btn small text color="error" :loading="deletingId === policy.id" @click="confirmRemove(policy)">Remover</v-btn>
          <v-btn small text color="primary" @click="openDialog(policy)">Editar</v-btn>
          <v-btn v-if="policy.status !== 'published'" small color="primary" @click="publish(policy)">Publicar</v-btn>
        </div>
      </article>
      <section v-if="!loading && !policies.length" class="data-panel empty-module">
        <strong>Nenhuma política criada.</strong>
        <p>Use um modelo acima para publicar as primeiras regras de revisão humana, dados sensíveis e uso externo.</p>
      </section>
    </section>

    <v-dialog v-model="dialog" max-width="680">
      <v-card>
        <v-card-title>{{ editing ? 'Editar política' : 'Nova política' }}</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.title" label="Nome da regra" outlined dense />
          <div class="form-grid">
            <v-select v-model="form.category" :items="categoryOptions" label="Categoria" outlined dense />
            <v-text-field v-model="form.owner_role" label="Responsável" outlined dense />
          </div>
          <div class="form-grid">
            <v-text-field v-model="form.version" label="Versão" outlined dense />
            <v-select v-model="form.status" :items="statusOptions" label="Status" outlined dense />
          </div>
          <v-textarea v-model="form.summary" label="Regra operacional" outlined dense rows="6" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn outlined color="primary" :loading="saving" @click="save('draft')">Salvar rascunho</v-btn>
          <v-btn color="primary" :loading="saving" @click="save('published')">Publicar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="460">
      <v-card>
        <v-card-title>Remover política</v-card-title>
        <v-card-text>
          <p class="mb-0">Esta política deixará de ser usada nas regras operacionais. Deseja remover?</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancelar</v-btn>
          <v-btn color="error" :loading="deleting" @click="removePolicy">Remover</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </section>
</template>

<script>
import api from '../../plugins/axios'
import { billingErrorMessage } from '../../utils/apiErrors'

const emptyForm = () => ({
  title: '',
  category: 'confidentiality',
  owner_role: 'Jurídico',
  version: '1.0',
  status: 'draft',
  summary: ''
})

export default {
  name: 'PoliciesPage',
  data () {
    return {
      policies: [],
      dialog: false,
      editing: null,
      form: emptyForm(),
      loading: false,
      saving: false,
      deleting: false,
      deletingId: null,
      deleteDialog: false,
      policyToDelete: null,
      error: '',
      categoryOptions: [
        { text: 'Confidencialidade', value: 'confidentiality' },
        { text: 'Revisão humana', value: 'human_review' },
        { text: 'Dados pessoais', value: 'personal_data' },
        { text: 'Uso em documentos jurídicos', value: 'legal_work_product' },
        { text: 'Uso externo', value: 'external_use' }
      ],
      statusOptions: [
        { text: 'Rascunho', value: 'draft' },
        { text: 'Publicada', value: 'published' },
        { text: 'Arquivada', value: 'archived' }
      ],
      policyTemplates: [
        {
          icon: 'mdi-account-check-outline',
          title: 'Revisão humana obrigatória',
          short: 'Documentos externos precisam de responsável identificado.',
          category: 'human_review',
          owner_role: 'Jurídico',
          summary: 'Todo documento que será enviado a cliente, contraparte, tribunal ou terceiro deve passar por revisão humana antes do uso. A decisão deve registrar responsável, data, ressalvas e versão revisada.'
        },
        {
          icon: 'mdi-account-lock-outline',
          title: 'Dados de cliente e dados pessoais',
          short: 'Dados sensíveis exigem minimização ou anonimização.',
          category: 'personal_data',
          owner_role: 'DPO',
          summary: 'Documentos com dados pessoais, dados sensíveis, informações de cliente, valores, estratégia processual ou cláusulas confidenciais exigem minimização, anonimização ou aprovação do responsável definido.'
        },
        {
          icon: 'mdi-gavel',
          title: 'Peças processuais e pareceres',
          short: 'Fontes jurídicas precisam ser conferidas antes do protocolo ou envio.',
          category: 'legal_work_product',
          owner_role: 'Jurídico',
          summary: 'Petições, pareceres, contratos, notificações e respostas a cliente devem ter fundamentos, citações, jurisprudência, cálculos e fatos conferidos em fonte oficial antes de qualquer envio, protocolo ou assinatura.'
        },
        {
          icon: 'mdi-send-lock-outline',
          title: 'Uso externo de documentos',
          short: 'Envio para fora do escritório exige decisão liberada.',
          category: 'external_use',
          owner_role: 'Gestor responsável',
          summary: 'Qualquer documento destinado a cliente, contraparte, autoridade, tribunal, fornecedor ou publicação externa deve ser validado no ComplyOps. Se houver risco médio ou alto, deve entrar na fila de revisão humana.'
        }
      ]
    }
  },
  created () {
    this.load()
  },
  methods: {
    async load () {
      this.loading = true
      this.error = ''
      try {
        const { data } = await api.get('/governance-policies')
        this.policies = data.filter(policy => policy.status !== 'archived')
      } catch (error) {
        this.error = billingErrorMessage(error, 'Não foi possível carregar políticas.')
      } finally {
        this.loading = false
      }
    },
    openDialog (policy, template) {
      this.editing = policy || null
      this.form = policy
        ? { ...emptyForm(), ...policy }
        : { ...emptyForm(), ...(template || {}) }
      if (template && !policy) {
        this.form.status = 'published'
      }
      this.dialog = true
    },
    async save (status) {
      this.saving = true
      this.error = ''
      try {
        const payload = { ...this.form, status: status || this.form.status }
        if (this.editing) {
          await api.put(`/governance-policies/${this.editing.id}`, payload)
        } else {
          await api.post('/governance-policies', payload)
        }
        this.dialog = false
        await this.load()
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível salvar política.'
      } finally {
        this.saving = false
      }
    },
    async publish (policy) {
      await api.put(`/governance-policies/${policy.id}`, { ...policy, status: 'published' })
      await this.load()
    },
    confirmRemove (policy) {
      this.policyToDelete = policy
      this.deleteDialog = true
      this.error = ''
    },
    async removePolicy () {
      if (!this.policyToDelete) return

      this.deleting = true
      this.deletingId = this.policyToDelete.id
      this.error = ''
      try {
        await api.put(`/governance-policies/${this.policyToDelete.id}`, {
          ...this.policyToDelete,
          status: 'archived'
        })
        this.deleteDialog = false
        this.policyToDelete = null
        await this.load()
      } catch (error) {
        this.error = error.response && error.response.data ? error.response.data.message : 'Não foi possível remover política.'
      } finally {
        this.deleting = false
        this.deletingId = null
      }
    },
    statusLabel (status) {
      return { draft: 'Rascunho', published: 'Publicada', archived: 'Arquivada' }[status] || status
    },
    categoryLabel (category) {
      return {
        confidentiality: 'Confidencialidade',
        human_review: 'Revisão humana',
        personal_data: 'Dados pessoais',
        legal_work_product: 'Documentos jurídicos',
        external_use: 'Uso externo',
        vendors: 'Fornecedores'
      }[category] || category
    }
  }
}
</script>
